import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Skeleton,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import { useSelector } from "react-redux";
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
} from "recharts";
import { useEffect, useState } from "react";
import { getRequest } from "../../globalhelper/helper";

const COLORS = ["#10b981", "#6366f1", "#8b5cf6", "#f59e0b", "#3b82f6", "#ec4899", "#06b6d4", "#ef4444"];

const CostCenterDashboard = ({ open, setOpen, company, peId, fromDate, toDate }) => {
    const [data, setData] = useState([]);
    const [chartType, setChartType] = useState("pie");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;
        if (company) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const formattedFromDate = fromDate ? fromDate.format("DD-MM-YYYY") : "";
                    const formattedToDate = toDate ? toDate.format("DD-MM-YYYY") : "";
                    let { res } = await getRequest(`api/dashboard/getCostCenterAnalysis?orgid=${company.orgid}&peid=${peId || ""}&fromDate=${formattedFromDate}&toDate=${formattedToDate}`);
                    let parsed = JSON.parse(res.data || "[]");

                    if (isMounted && parsed.length) {
                        const total = parsed.reduce((sum, item) => sum + (parseFloat(item.totalgoodsreceipt) || 0), 0);
                        const mappedData = parsed.map((item, index) => {
                            const val = parseFloat(item.totalgoodsreceipt) || 0;
                            const percentage = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";

                            let displayValue = val;
                            if (val >= 10000000) displayValue = (val / 10000000).toFixed(2) + " Cr";
                            else if (val >= 100000) displayValue = (val / 100000).toFixed(2) + " L";
                            else displayValue = val.toLocaleString("en-IN");

                            return {
                                name: item.costallocationmethod,
                                value: val,
                                percentage,
                                displayValue,
                                fullLabel: `${displayValue} (${percentage}%)`,
                                color: COLORS[index % COLORS.length],
                            };
                        });
                        setData(mappedData);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    if (isMounted) setLoading(false);
                }
            };
            fetchData();
        }
        return () => { isMounted = false; };
    }, [company]);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            return (
                <Box sx={{
                    bgcolor: "rgba(255,255,255,0.96)",
                    p: 1.2,
                    border: "1px solid #f1f5f9",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.78rem" }}>
                            {d.name}
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: "#475569", fontSize: "0.75rem", display: "block", mt: 0.3, pl: 2.5 }}>
                        {d.displayValue} &bull; {d.percentage}%
                    </Typography>
                </Box>
            );
        }
        return null;
    };
    const CustomizedAxisTick = (props) => {
        const { x, y, payload } = props;
        const text = payload.value || "";

        if (!open) {
            const displayedText = text.length > 20 ? text.slice(0, 20) + "..." : text;
            return (
                <g transform={`translate(${x},${y})`}>
                    <text x={0} y={0} dy={16} textAnchor="end" fill="#475569" transform="rotate(-35)" fontSize={open ? 14 : 10}>
                        {displayedText}
                    </text>
                </g>
            );
        }

        const words = text.split(" ");
        const lines = [];
        let currentLine = "";
        words.forEach(word => {
            if ((currentLine + word).length > 20) {
                if (currentLine) lines.push(currentLine.trim());
                currentLine = word + " ";
            } else {
                currentLine += word + " ";
            }
        });
        if (currentLine) lines.push(currentLine.trim());

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="end" fill="#475569" transform="rotate(-35)" fontSize={open ? 14 : 10}>
                    {lines.map((line, i) => (
                        <tspan key={i} x={0} dy={i === 0 ? 0 : "1.1em"}>{line}</tspan>
                    ))}
                </text>
            </g>
        );
    };


    return (
        <Card sx={{
            height: open ? "85vh" : 460,
            minHeight: open ? "85vh" : 460,
            flexGrow: 1,
            borderRadius: 4,
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            border: "1px solid #f1f5f9",
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 10px 30px rgba(0,0,0,0.08)" },
        }}>
            <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", p: 3 }}>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#475569", fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 1.2 }}>
                        <Box sx={{ p: 0.8, bgcolor: "#f5f3ff", borderRadius: 2, display: "flex" }}>
                            <DonutLargeIcon sx={{ color: "#8b5cf6", fontSize: "1.2rem" }} />
                        </Box>
                        Cost Center Analysis
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={(_, val) => val && setChartType(val)}
                            size="small"
                            sx={{ "& .MuiToggleButton-root": { px: 1, py: 0.3, borderRadius: 2 } }}
                        >
                            <ToggleButton value="pie" title="Pie Chart">
                                <PieChartOutlineIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="bar" title="Bar Chart">
                                <BarChartIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {!open && (
                            <IconButton onClick={() => setOpen((prev) => ({ ...prev, costCenter: !prev.costCenter }))}>
                                <OpenInFullIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Chart Area */}
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    {loading ? (
                        chartType === "pie" ? (
                            <Grid container spacing={2} alignItems="center" sx={{ height: "100%" }}>
                                <Grid item xs={4}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: 1 }}>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                                    <Skeleton variant="circular" width={10} height={10} />
                                                    <Skeleton width={100} height={16} />
                                                </Box>
                                                <Skeleton width={30} height={14} />
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                                <Grid item xs={8}>
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 350 }}>
                                        <Skeleton variant="circular" width={open ? 240 : 160} height={open ? 240 : 160} />
                                    </Box>
                                </Grid>
                            </Grid>
                        ) : (
                            <Box sx={{ height: "100%", pt: 2 }}>
                                <Skeleton variant="rectangular" width="100%" height={open ? 420 : 300} sx={{ borderRadius: 3 }} />
                                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, px: 2 }}>
                                    {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} width={60} height={15} />)}
                                </Box>
                            </Box>
                        )
                    ) : data.length > 0 ? (
                        chartType === "pie" ? (
                            <Grid container spacing={1} sx={{ height: "100%" }} alignItems="center">
                                {/* Legend */}
                                <Grid item xs={4}>
                                    <Box sx={{ pl: 1 }}>
                                        {data.map((item) => (
                                            <Box key={item.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: open ? 1.5 : 1 }}>
                                                    <Box sx={{ width: open ? 12 : 8, height: open ? 12 : 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                                                    <Typography variant="caption" sx={{ color: "#334155", fontWeight: open ? 700 : 600, fontSize: open ? "1rem" : "0.72rem", lineHeight: 1.3 }}>
                                                        {item.name.length > 20 && !open ? item.name.slice(0, 20) + "…" : item.name}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700, fontSize: open ? "1rem" : "0.72rem", ml: 1, flexShrink: 0 }}>
                                                    {item.percentage}%
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                                {/* Pie */}
                                <Grid item xs={8}>
                                    <Box sx={{ height: 350, width: "100%" }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={data}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={open ? 80 : 50}
                                                    outerRadius={open ? 120 : 80}
                                                    paddingAngle={4}
                                                    dataKey="value"
                                                    label={(props) => {
                                                        const { cx, cy, midAngle, innerRadius, outerRadius, value, index, payload, x, y, textAnchor } = props;
                                                        return (
                                                            <text
                                                                x={x}
                                                                y={y}
                                                                fill="#475569"
                                                                textAnchor={textAnchor}
                                                                dominantBaseline="central"
                                                                fontSize={open ? 14 : 11}
                                                                fontWeight={700}
                                                            >
                                                                {`${payload.percentage}% - ${payload.displayValue}`}
                                                            </text>
                                                        );
                                                    }}
                                                    labelLine={false}
                                                >
                                                    {data.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Grid>
                            </Grid>
                        ) : (
                            // Bar Chart
                            <Box sx={{ height: open ? 700 : "100%", width: "100%", mt: 1 }}>
                                <ResponsiveContainer width="100%" height={open ? 420 : "100%"}>
                                    <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
                                        <CartesianGrid strokeDasharray="8 4" vertical={false} />
                                        <XAxis
                                            dataKey="name"
                                            interval={0}
                                            tick={<CustomizedAxisTick />}
                                            height={open ? 120 : 70}
                                        />
                                        <YAxis
                                            tickFormatter={(v) =>
                                                v >= 10000000 ? `${(v / 10000000).toFixed(1)}Cr`
                                                    : v >= 100000 ? `${(v / 100000).toFixed(1)}L`
                                                        : v.toLocaleString("en-IN")
                                            }
                                            tick={{ fontSize: open ? 14 : 10, fill: "#334155", fontWeight: 700 }}
                                            width={open ? 80 : 60}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={open ? 48 : 32}>
                                            {data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                            <LabelList
                                                dataKey="fullLabel"
                                                position="top"
                                                style={{ fontSize: open ? 14 : 10, fill: "#334155", fontWeight: 700 }}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        )
                    ) : (
                        <Box sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8",
                            gap: 1
                        }}>
                            <PieChartOutlineIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                No data found
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default CostCenterDashboard;
